import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import { getLocalisedDate } from '../common/datetime/FormattedDateTime';
import { InternalApiFinalRisk, InternalApiFinalRiskLevel, InternalApiFinalRiskRangeEntry } from '../api-internal';
import { formatEnumLabel } from '../common/helper';
import { RiskHistoryDateRange } from './riskHistoryFilters';
import { buildRiskChartModel, LevelThreshold, OverridePeriod, PointType } from './riskChartData';
import { RISK_LEVEL_CSS_VARS } from './riskLevelColours';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartFft } from '@fortawesome/pro-solid-svg-icons';

ChartJS.register(LinearScale, PointElement, LineElement, Filler, Tooltip);

const resolveCssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const WHITE_COLOUR = () => resolveCssVar('--brand-snow');
const DEFAULT_COLOUR = () => resolveCssVar('--secondary-active');

const colourForLevel = (level: InternalApiFinalRiskLevel): { line: string; fill: string } => {
  const colour = resolveCssVar(RISK_LEVEL_CSS_VARS[level]);
  return {
    line: colour,
    fill: `${colour}33`,
  };
};

const pointTooltip = (type: PointType, level: InternalApiFinalRiskLevel): string => {
  if (type === PointType.SCORE_CHANGED) {
    return 'Score has changed';
  } else if (type === PointType.OVERRIDE_STARTED) {
    return `Override to ${formatEnumLabel(level)} has started`;
  } else if (type === PointType.OVERRIDE_ENDED) {
    return `Override to ${formatEnumLabel(level)} has ended`;
  } else if (type === PointType.END_RISK) {
    return 'Risk score at the end of the period';
  } else {
    return 'Risk score at the start of the period';
  }
};

interface OverrideMarkersPluginOptions {
  periods: OverridePeriod[];
}

interface LevelLinesPluginOptions {
  thresholds: LevelThreshold[];
}

const OVERRIDE_MARKERS_PLUGIN_ID = 'overrideMarkers';
const OVERRIDE_RIBBON_HEIGHT = 6;
const OVERRIDE_RIBBON_OFFSET = 16;
const OVERRIDE_RIBBON_LABEL_OFFSET = 56;

const overrideMarkersPlugin: Plugin<'line', OverrideMarkersPluginOptions> = {
  id: OVERRIDE_MARKERS_PLUGIN_ID,
  beforeDatasetsDraw(chart, _args, options) {
    const { periods } = options;
    if (!periods?.length) {
      return;
    }
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) {
      return;
    }

    ctx.save();

    for (const period of periods) {
      const colour = colourForLevel(period.level).line;
      const periodStart = xScale.getPixelForValue(period.start);
      const periodEnd = period.end != null ? xScale.getPixelForValue(period.end) : chartArea.right;
      const clampedStart = Math.max(periodStart, chartArea.left);
      const clampedEnd = Math.min(periodEnd, chartArea.right);
      if (clampedStart < clampedEnd) {
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.roundRect(
          clampedStart,
          chartArea.top - OVERRIDE_RIBBON_HEIGHT - OVERRIDE_RIBBON_OFFSET,
          clampedEnd - clampedStart,
          OVERRIDE_RIBBON_HEIGHT,
          3
        );
        ctx.fill();
      }
      drawMarker(ctx, chartArea, periodStart, colour);
      if (period.end != null) {
        drawMarker(ctx, chartArea, periodEnd, colour);
      }
    }
    ctx.fillStyle = DEFAULT_COLOUR();
    ctx.fillText('Overrides', chartArea.left - OVERRIDE_RIBBON_LABEL_OFFSET, chartArea.top - OVERRIDE_RIBBON_OFFSET);
    ctx.restore();
  },
};

const drawMarker = (
  ctx: CanvasRenderingContext2D,
  chartArea: { top: number; bottom: number; left: number; right: number },
  x: number,
  colour: string
) => {
  if (x < chartArea.left || x > chartArea.right) {
    return;
  }
  ctx.beginPath();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1;
  ctx.moveTo(x, chartArea.top);
  ctx.lineTo(x, chartArea.bottom);
  ctx.stroke();
};

const LEVEL_LINES_PLUGIN_ID = 'levelLines';

const levelLinesPlugin: Plugin<'line', LevelLinesPluginOptions> = {
  id: LEVEL_LINES_PLUGIN_ID,
  beforeDatasetsDraw(chart, _args, options) {
    const { thresholds } = options;
    if (!thresholds?.length) {
      return;
    }
    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale) {
      return;
    }

    ctx.save();
    ctx.font = '10px sans-serif';
    ctx.textBaseline = 'bottom';
    for (const t of thresholds) {
      const y = yScale.getPixelForValue(t.score);
      if (y < chartArea.top || y > chartArea.bottom) {
        continue;
      }
      const colour = colourForLevel(t.level).line;
      ctx.beginPath();
      ctx.setLineDash([2, 4]);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1;
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.fillStyle = colour;
      ctx.fillText(formatEnumLabel(t.level), chartArea.left + 4, y - 1);
    }
    ctx.restore();
  },
};

ChartJS.register(overrideMarkersPlugin, levelLinesPlugin);

interface Props {
  events: InternalApiFinalRisk[];
  ranges?: InternalApiFinalRiskRangeEntry[];
  dateRange: RiskHistoryDateRange;
}

export const RiskHistoryChart = ({ events, ranges, dateRange }: Props) => {
  if (!ranges) {
    return null;
  }

  const { scorePoints, overrides, thresholds } = buildRiskChartModel(events, ranges, dateRange, dayjs().valueOf());

  if (scorePoints.length < 2) {
    return (
      <div style={{ margin: '16px 8px', color: 'var(--text-soft)' }}>
        <FontAwesomeIcon icon={faChartFft} /> There is not enough events in the selected range for a chart.
      </div>
    );
  }

  const segmentBackgroundColor = (ctx: { p1DataIndex: number }) =>
    colourForLevel(scorePoints[ctx.p1DataIndex - 1]?.effectiveSegmentLevel).fill;
  const data = scorePoints.map((p) => ({ x: p.time, y: p.score }));

  const chartData: ChartData<'line'> = {
    datasets: [
      {
        data,
        borderColor: DEFAULT_COLOUR(),
        fill: 'start',
        stepped: 'before',
        pointRadius: 4,
        pointHoverRadius: 4,
        pointBackgroundColor: WHITE_COLOUR(),
        pointBorderColor: DEFAULT_COLOUR(),
        borderWidth: 2,
        segment: {
          backgroundColor: segmentBackgroundColor,
        },
      },
      // Second dataset is only needed to do fill: 'start' and fill: 'end' at the same time
      {
        data,
        fill: 'end',
        stepped: 'before',
        pointRadius: 0,
        segment: {
          backgroundColor: segmentBackgroundColor,
        },
      },
    ],
  };

  const min = data.at(0)!.x;
  const max = data.at(-1)!.x;
  const width = max - min;

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        filter: (item) => item.datasetIndex === 0,
        callbacks: {
          title: (items) => getLocalisedDate(dayjs(items[0]?.parsed?.x).toString()),
          label: (item) => `Score: ${item.parsed.y}`,
          footer: (items) =>
            pointTooltip(scorePoints[items[0].dataIndex].type, scorePoints[items[0].dataIndex].effectiveLevel),
        },
      },
      // @ts-expect-error custom plugin options are not part of the chart.js types
      [OVERRIDE_MARKERS_PLUGIN_ID]: { periods: overrides },
      [LEVEL_LINES_PLUGIN_ID]: { thresholds },
    },
    scales: {
      x: {
        type: 'linear',
        grid: { display: false },
        ticks: {
          maxTicksLimit: 6,
          font: { size: 11 },
          autoSkip: true,
          callback: (value) => getLocalisedDate(dayjs(value as number).toString()),
        },
        min: min - width / 20,
        max: max + width / 20,
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 11 } },
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
      },
    },
    layout: {
      padding: {
        top: OVERRIDE_RIBBON_OFFSET + OVERRIDE_RIBBON_HEIGHT * 2,
        left: 30,
      },
    },
  };

  return (
    <div style={{ margin: '8px 0' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default RiskHistoryChart;
